import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Camera, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { GENRES } from "@/lib/constants";
import { fileToResizedDataUrl } from "@/lib/image";
import Avatar from "@/components/Avatar";
import Reveal from "@/components/Reveal";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Switch } from "@/components/ui/switch";

const PRIVACY_FIELDS = [
  ["show_location", "Show my city & state"],
  ["show_bio", "Show my bio"],
  ["show_genres", "Show my favorite genres"],
  ["show_reading", "Show my reading preferences"],
  ["show_currently_reading", "Show what I'm currently reading"],
  ["show_attendance", "Show gatherings I'm attending"],
];

const Text = ({ label, value, onChange, placeholder, testid, textarea }) => (
  <div>
    <label className="text-cream/50 text-xs uppercase tracking-wider block mb-1">{label}</label>
    {textarea
      ? <textarea rows={3} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="input-line resize-none" data-testid={testid} />
      : <input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder} className="input-line" data-testid={testid} />}
  </div>
);

export default function EditProfile() {
  const navigate = useNavigate();
  const { user, refresh } = useAuth();
  const fileRef = useRef();
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/profile/me").then(({ data }) => setF(data)); }, []);
  const set = (k) => (v) => setF({ ...f, [k]: v });
  const toggleGenre = (g) => setF({ ...f, genres: (f.genres || []).includes(g) ? f.genres.filter((x) => x !== g) : [...(f.genres || []), g] });
  const setPriv = (k, v) => setF({ ...f, privacy: { ...(f.privacy || {}), [k]: v } });

  const onPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { const dataUrl = await fileToResizedDataUrl(file); setF((prev) => ({ ...prev, picture: dataUrl })); }
    catch (err) { toast.error(err.message); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        first_name: f.first_name, last_name: f.last_name, display_name: f.display_name,
        city: f.city, state: f.state, bio: f.bio, birthday: f.birthday,
        genres: f.genres || [], favorite_book: f.favorite_book, favorite_author: f.favorite_author,
        currently_reading: f.currently_reading, want_to_read: f.want_to_read,
        book_that_changed_me: f.book_that_changed_me, favorite_quote: f.favorite_quote,
        picture: f.picture || "", privacy: f.privacy || {}, directory_visible: f.directory_visible,
      };
      await api.put("/profile", payload);
      await refresh();
      toast.success("Your profile has been saved.");
      navigate(`/members/${user.id}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!f) return <div className="min-h-screen bg-mahogany-900" />;

  return (
    <div className="min-h-screen bg-gradient-to-b from-mahogany-950 to-mahogany-900 pt-32 pb-20 px-6 grain" data-testid="edit-profile-page">
      <div className="max-w-2xl mx-auto">
        <Reveal className="mb-10">
          <p className="font-script text-3xl text-fuchsia mb-1">Make yourself at home.</p>
          <h1 className="font-serif text-5xl text-cream">Edit Your Profile</h1>
        </Reveal>

        <Tabs defaultValue="about">
          <TabsList className="bg-transparent flex flex-wrap gap-2 mb-8 h-auto justify-start" data-testid="edit-tabs">
            {[["about", "About Me"], ["reading", "My Reading Life"], ["privacy", "Privacy & Visibility"], ["account", "Account"]].map(([v, l]) => (
              <TabsTrigger key={v} value={v} data-testid={`edit-tab-${v}`} className="rounded-full border border-gold/30 px-5 py-2 text-cream/70 text-xs uppercase tracking-wider data-[state=active]:bg-gold data-[state=active]:text-mahogany-900">{l}</TabsTrigger>
            ))}
          </TabsList>

          <TabsContent value="about">
            <div className="bg-mahogany-950 gold-frame rounded-sm p-8 space-y-6">
              <div className="flex items-center gap-6">
                <Avatar src={f.picture} name={f.display_name} size={96} />
                <div className="flex gap-3">
                  <input ref={fileRef} type="file" accept="image/*" hidden onChange={onPhoto} data-testid="photo-input" />
                  <button onClick={() => fileRef.current?.click()} className="btn-secondary !py-2 !px-4 !text-xs inline-flex items-center gap-2" data-testid="photo-upload"><Camera size={14} /> {f.picture ? "Replace" : "Upload"}</button>
                  {f.picture && <button onClick={() => set("picture")("")} className="border border-red-400/40 text-red-300 rounded-sm !py-2 !px-4 text-xs uppercase tracking-wider inline-flex items-center gap-2" data-testid="photo-remove"><Trash2 size={14} /> Remove</button>}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Text label="First Name" value={f.first_name} onChange={set("first_name")} testid="edit-first-name" />
                <Text label="Last Name" value={f.last_name} onChange={set("last_name")} testid="edit-last-name" />
              </div>
              <Text label="Display Name" value={f.display_name} onChange={set("display_name")} placeholder="How the Room sees you" testid="edit-display-name" />
              <div className="grid sm:grid-cols-2 gap-5">
                <Text label="City" value={f.city} onChange={set("city")} testid="edit-city" />
                <Text label="State" value={f.state} onChange={set("state")} testid="edit-state" />
              </div>
              <Text label="About Me" value={f.bio} onChange={set("bio")} placeholder="Tell the Room about your reading life…" testid="edit-bio" textarea />
              <div>
                <Text label="Birthday (private by default)" value={f.birthday} onChange={set("birthday")} placeholder="YYYY-MM-DD" testid="edit-birthday" />
                <p className="text-cream/40 text-xs mt-1">Your birthday is never shown to other members.</p>
              </div>
            </div>
          </TabsContent>

          <TabsContent value="reading">
            <div className="bg-mahogany-950 gold-frame rounded-sm p-8 space-y-6">
              <div>
                <label className="text-cream/50 text-xs uppercase tracking-wider block mb-3">Favorite Genres</label>
                <div className="flex flex-wrap gap-2">
                  {GENRES.map((g) => (
                    <button type="button" key={g} onClick={() => toggleGenre(g)} data-testid={`edit-genre-${g.slice(0, 6).toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${(f.genres || []).includes(g) ? "bg-fuchsia border-fuchsia text-white" : "border-gold/30 text-cream/70 hover:border-gold"}`}>{g}</button>
                  ))}
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-5">
                <Text label="Favorite Book" value={f.favorite_book} onChange={set("favorite_book")} testid="edit-fav-book" />
                <Text label="Favorite Author" value={f.favorite_author} onChange={set("favorite_author")} testid="edit-fav-author" />
              </div>
              <Text label="Currently Reading" value={f.currently_reading} onChange={set("currently_reading")} testid="edit-currently" />
              <Text label="Books I Want to Read" value={f.want_to_read} onChange={set("want_to_read")} testid="edit-want" />
              <Text label="A Book That Changed Me" value={f.book_that_changed_me} onChange={set("book_that_changed_me")} testid="edit-changed" />
              <Text label="Favorite Quote (optional)" value={f.favorite_quote} onChange={set("favorite_quote")} testid="edit-quote" textarea />
            </div>
          </TabsContent>

          <TabsContent value="privacy">
            <div className="bg-mahogany-950 gold-frame rounded-sm p-8 space-y-5" data-testid="privacy-panel">
              <p className="text-cream/60 text-sm">The Mahogany Room is a safe space. You choose what other members can see. Your email is never shown.</p>
              {PRIVACY_FIELDS.map(([k, label]) => (
                <div key={k} className="flex items-center justify-between border-b border-gold/10 pb-4">
                  <span className="text-cream/80 text-sm">{label}</span>
                  <Switch checked={(f.privacy || {})[k] ?? (k !== "show_attendance")} onCheckedChange={(v) => setPriv(k, v)} data-testid={`priv-${k}`} />
                </div>
              ))}
            </div>
          </TabsContent>

          <TabsContent value="account">
            <div className="bg-mahogany-950 gold-frame rounded-sm p-8 space-y-4" data-testid="account-panel">
              <div><p className="text-cream/50 text-xs uppercase tracking-wider">Email (private)</p><p className="text-cream">{f.email}</p></div>
              <div><p className="text-cream/50 text-xs uppercase tracking-wider">Member Since</p><p className="text-cream">{f.created_at ? new Date(f.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" }) : "—"}</p></div>
              <div><p className="text-cream/50 text-xs uppercase tracking-wider">Account Status</p><p className="text-cream capitalize">{f.account_status || "active"}</p></div>
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex gap-3 mt-8">
          <button onClick={save} disabled={busy} className="btn-primary" data-testid="save-profile">{busy ? "Saving…" : "Save Changes"}</button>
          <button onClick={() => navigate(`/members/${user.id}`)} className="btn-secondary inline-flex items-center gap-2" data-testid="cancel-profile"><X size={16} /> Cancel</button>
        </div>
      </div>
    </div>
  );
}
